import { CurrencyPipe } from '@angular/common';
import { Component, effect, inject, input, model, output, signal, untracked } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonRadio,
  IonRadioGroup,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxTextFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { VxDynamicImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { dataFromState, initialState } from '@libs/core/lib/state/async-states';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import {
  withCreatePaymentIntent,
  withExchangeRate,
  withPaymentIntentConfirm,
} from '@libs/mtjr/shared/generated/entities/payment-intent/payment-intent-store-features';
import { Enums, TopUpPaymentMethodList } from '@libs/mtjr/shared/generated/types';
import { patchState, signalStore, withComputed, withMethods } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import Decimal from 'decimal.js';

const PaymentIntentStore = signalStore(
  withCreatePaymentIntent(),
  withExchangeRate(),
  withPaymentIntentConfirm(),
  withComputed((store) => ({
    usdExchangeRate: dataFromState(store.exchangeRateState, (data) => data?.usd),
  })),
  withMethods((store) => ({
    resetPaymentIntentState() {
      patchState(store, { createPaymentIntentState: initialState() });
    },
  })),
);

@Component({
  selector: 'mtjr-payment-method-selector',
  providers: [PaymentIntentStore],
  imports: [
    ReactiveFormsModule,
    IonRadioGroup,
    IonItem,
    IonIcon,
    IonLabel,
    IonText,
    IonRadio,
    IonModal,
    IonContent,
    IonButton,
    IonButtons,
    IonHeader,
    IonTitle,
    IonToolbar,
    TranslatePipe,
    CurrencyPipe,
    VxCurrencyPipe,
    VxDynamicImageComponent,
    VxTextFieldComponent,
    VxLoadingComponent,
    VxErrorComponent,
    VxErrorBarComponent,
    VxSubmitButtonComponent,
  ],
  templateUrl: './payment-method-selector.component.html',
  styleUrls: ['./payment-method-selector.component.scss'],
})
export class PaymentMethodSelectorComponent {
  TopUpPaymentMethodList = TopUpPaymentMethodList;
  paymentMethodIcons: Record<Enums.TopUpPaymentMethod, string> = {
    crypto: 'w-pay',
    moamalat: 'moamalat',
    yussorPay: 'yussor-pay',
    masrafyPay: 'masrafy-pay',
  };

  // Injected services
  private paymentIntentStore = inject(PaymentIntentStore);
  private alerts = inject(AppAlerts);
  private fb = inject(NonNullableFormBuilder);

  // Inputs
  selectedMethod = model<Enums.TopUpPaymentMethod | undefined>(undefined);
  paymentIntentType = input.required<Enums.PaymentIntentType>();
  isModalOpen = signal(false);
  modalTitle = input(''); // e.g., 'WALLET.TOP_UP' | translate
  submitButtonLabel = input(''); // e.g., 'WALLET.TOP_UP' | translate
  amountField = this.fb.control<number | null>(null, {
    validators: [Validators.required, Validators.min(1)],
  });

  identityCardField = this.fb.control<string | null>(null, {
    validators: [Validators.minLength(9), Validators.required],
  });

  otpField = this.fb.control<string | null>(null, {
    validators: [Validators.minLength(4), Validators.required],
  });

  amount = input<Decimal | undefined, Decimal | number | undefined>(undefined, {
    transform: (value) => {
      if (value === undefined) return value;
      return new Decimal(value);
    },
  });

  orderId = input<string>();

  // Computed values
  valueInUSD = signal(new Decimal(0));
  usdExchangeRate = this.paymentIntentStore.usdExchangeRate;
  amountWithFee = signal(new Decimal(0));

  // Exchange rate state
  exchangeRateState = this.paymentIntentStore.exchangeRateState;
  exchangeRateFailure = this.paymentIntentStore.exchangeRateFailure;

  // Checkout modal state
  checkoutModalOpen = signal(false);
  checkoutUrl = signal<string | undefined | null>(undefined);
  responseMetadata = signal<Record<string, unknown> | undefined | null>(undefined);
  paymentIntentId = signal<string | undefined | null>(undefined);

  // Outputs for modal actions
  checkoutModalDismiss = output<void>();
  moamalatContinue = output<void>();

  modalDismissed = output<void>();

  // Expose store state for parent components
  createPaymentIntentState = this.paymentIntentStore.createPaymentIntentState;
  createPaymentIntentFailure = this.paymentIntentStore.createPaymentIntentFailure;
  createPaymentIntentLoading = this.paymentIntentStore.createPaymentIntentLoading;

  confirmState = this.paymentIntentStore.paymentIntentConfirmState;
  confirmFailure = this.paymentIntentStore.paymentIntentConfirmFailure;
  confirmLoading = this.paymentIntentStore.paymentIntentConfirmLoading;

  constructor() {
    // Fetch exchange rate on init
    this.paymentIntentStore.exchangeRate();

    // Calculate USD value and fee when amount changes
    this.amountField.valueChanges.subscribe((value) => {
      if (value) this.calculateAmountValues(new Decimal(value));
    });

    effect(() => {
      const amount = this.amount();

      if (amount) {
        this.amountField.setValue(amount.toNumber(), { emitEvent: false });
        this.calculateAmountValues(amount);
      }
    });

    // Handle payment intent creation success
    effect(() => {
      const confirmState = this.paymentIntentStore.paymentIntentConfirmState();
      if (confirmState.success) {
        this.modalDismissed.emit();
        this.resetPaymentIntentState();
      }
    });
    effect(() => {
      const state = this.paymentIntentStore.createPaymentIntentState();
      if (state.success) {
        this.responseMetadata.set(state.data.moamalatMetadata);
        this.checkoutUrl.set(state.data.checkoutUrl);
        this.paymentIntentId.set(state.data.paymentIntentId);
        untracked(() => {
          this.checkoutModalOpen.set(true);
        });
      }
    });
  }

  async createPaymentIntent() {
    const topUpPaymentMethod = this.selectedMethod();
    if (!topUpPaymentMethod) {
      this.alerts.warningSnackbarTR('WALLET.PAYMENT_METHOD_REQUIRED');
      return;
    }
    let amount = this.amount() ?? new Decimal(this.amountField.value ?? 0);

    if (amount.lessThanOrEqualTo(0)) {
      throw new Error('Amount must be greater than 0');
    }

    if (topUpPaymentMethod === 'moamalat') {
      amount = amount.mul(1.03);
    }

    if (topUpPaymentMethod === 'yussorPay' && !this.identityCardField.valid) {
      this.alerts.dangerSnackbarTR('WALLET.IDENTITY_CARD_REQUIRED');
      return;
    }

    await this.paymentIntentStore.createPaymentIntent({
      amount: amount,
      topUpPaymentMethod,
      type: this.paymentIntentType(),
      orderId: this.orderId(),
      identityCard: this.identityCardField.value ?? undefined,
    });
  }

  start() {
    this.resetPaymentIntentState();
    this.isModalOpen.set(true);
  }

  resetPaymentIntentState() {
    this.paymentIntentStore.resetPaymentIntentState();
    this.checkoutModalOpen.set(false);
    this.isModalOpen.set(false);
    this.amountField.reset();
    this.identityCardField.reset();
    this.otpField.reset();
    this.checkoutUrl.set(undefined);
    this.responseMetadata.set(undefined);
    this.paymentIntentId.set(undefined);
    this.selectedMethod.set(undefined);
    this.valueInUSD.set(new Decimal(0));
    this.amountWithFee.set(new Decimal(0));
  }

  onModalDismiss() {
    this.isModalOpen.set(false);
    this.modalDismissed.emit();
  }

  goToMoamalat() {
    (window as unknown as { Lightbox: { Checkout: { configure: unknown } } }).Lightbox.Checkout.configure = {
      ...this.responseMetadata(),
      completeCallback: (data: unknown) => {
        console.log('completed');
        console.log(data);
        const paymentIntentId = this.paymentIntentId();
        if (!paymentIntentId) {
          this.alerts.dangerSnackbarTR('WALLET.PAYMENT_INTENT_ID_REQUIRED');
          return;
        }
        this.paymentIntentStore.paymentIntentConfirm({
          topUpPaymentMethod: 'moamalat',
          metadata: { moamalat: data },
          paymentIntentId,
        });
      },
      errorCallback: (error: unknown) => {
        console.log('error');
        console.log(error);
      },
      cancelCallback: () => {
        console.log('cancelled');
      },
    };
    // (window as unknown as { Lightbox: { Checkout: { showLightbox: () => string } } }).Lightbox.Checkout.showLightbox();

    const url = (
      window as unknown as { Lightbox: { Checkout: { getLightBoxUrl: () => string } } }
    ).Lightbox.Checkout.getLightBoxUrl();
    this.openUrl(url);
  }

  private calculateAmountValues(amount: Decimal) {
    const usdExchangeRate = this.usdExchangeRate();
    if (amount && usdExchangeRate) {
      const usd = new Decimal(usdExchangeRate);
      const topUpAmount = new Decimal(amount);
      const usdAmountInCents = topUpAmount
        .dividedBy(usd)
        .toDecimalPlaces(2, Decimal.ROUND_CEIL)
        .mul(100)
        .trunc()
        .div(100);
      this.valueInUSD.set(usdAmountInCents);
      this.amountWithFee.set(amount.mul(1.03));
    } else {
      this.valueInUSD.set(new Decimal(0));
      this.amountWithFee.set(new Decimal(0));
    }
  }

  copyToClipboard(value: string) {
    navigator.clipboard.writeText(value);
    this.alerts.successSnackbarTR('COMMON.COPIED_TO_CLIPBOARD');
  }

  openUrl(url: string) {
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url, windowName: '_self', presentationStyle: 'popover' });
    } else {
      window.open(url, '_blank');
    }
  }

  confirmYussorPay() {
    const paymentIntentId = this.paymentIntentId();
    if (!paymentIntentId) {
      this.alerts.dangerSnackbarTR('WALLET.PAYMENT_INTENT_ID_REQUIRED');
      return;
    }

    this.paymentIntentStore.paymentIntentConfirm({
      paymentIntentId,
      topUpPaymentMethod: 'yussorPay',
      metadata: {
        masarat: {
          otp: this.otpField.value,
        },
      },
    });
  }
}
